import React, { useState, useEffect } from 'react';
import { AssetType, AssetTypeField } from '@/firebase/assetService';
import { ComplianceTemplate, complianceService } from '@/firebase/complianceService';
import { useOrganization } from '@/contexts/OrganizationContext';
import { getIndustryType } from '@/firebase/organizationService';
import { getAvailableFieldTypes } from '@/utils/assetFieldTypeHelpers';
import { X, Plus, Search, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

interface AssetTypeFormProps {
  assetType?: AssetType;
  onSubmit: (data: Partial<AssetType>) => void;
  onCancel: () => void;
}

const AssetTypeForm: React.FC<AssetTypeFormProps> = ({ assetType, onSubmit, onCancel }) => {
  const { currentOrganization } = useOrganization();
  const [formData, setFormData] = useState<Partial<AssetType>>({
    name: '',
    description: '',
    complianceTemplateIds: [],
    customFields: []
  });
  const [templates, setTemplates] = useState<ComplianceTemplate[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [complianceSearchTerm, setComplianceSearchTerm] = useState('');
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState<string>('text');
  const [newFieldDescription, setNewFieldDescription] = useState('');
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  
  // Get available field types for current organization
  const organizationType = getIndustryType(currentOrganization);
  const availableFieldTypes = getAvailableFieldTypes(organizationType);

  useEffect(() => {
    loadTemplates();
    if (assetType) {
      setFormData({
        name: assetType.name,
        description: assetType.description,
        complianceTemplateIds: assetType.complianceTemplateIds || [],
        customFields: assetType.customFields || []
      });
      setSelectedTemplateIds(assetType.complianceTemplateIds || []);
    }
  }, [assetType]);

  const loadTemplates = async () => {
    const result = await complianceService.getAllTemplates(currentOrganization?.id);
    if (result.success && result.data) {
      setTemplates(result.data);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Please enter an asset type name');
      return;
    }
    
    const dataToSubmit = {
      ...formData,
      complianceTemplateIds: selectedTemplateIds,
      customFields: formData.customFields
    };
    
    onSubmit(dataToSubmit);
  };

  const addField = () => {
    if (!newFieldName.trim()) {
      alert('Please enter a field name');
      return;
    }
    
    // Get field type config to copy metadata
    const fieldTypeConfig = availableFieldTypes.find(ft => ft.type === newFieldType);
    
    const newField: AssetTypeField = {
      name: newFieldName.trim(),
      type: newFieldType,
      required: newFieldRequired,
      description: newFieldDescription.trim() || undefined,
      // Add metadata for custom field types
      metadata: (fieldTypeConfig?.roleFilter || fieldTypeConfig?.validation || fieldTypeConfig?.component) ? {
        ...(fieldTypeConfig.roleFilter && { roleFilter: fieldTypeConfig.roleFilter }),
        ...(organizationType && { organizationType }),
        ...(fieldTypeConfig.component && { component: fieldTypeConfig.component }),
        ...(fieldTypeConfig.validation && { validation: fieldTypeConfig.validation })
      } : undefined
    };
    
    setFormData({
      ...formData,
      customFields: [...(formData.customFields || []), newField]
    });
    
    // Reset form
    setNewFieldName('');
    setNewFieldType('text');
    setNewFieldDescription('');
    setNewFieldRequired(false);
  };

  const removeField = (index: number) => {
    const updatedFields = [...(formData.customFields || [])];
    updatedFields.splice(index, 1);
    setFormData({
      ...formData,
      customFields: updatedFields
    });
  };

  const moveField = (index: number, direction: 'up' | 'down') => {
    const updatedFields = [...(formData.customFields || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < updatedFields.length) {
      [updatedFields[index], updatedFields[newIndex]] = [updatedFields[newIndex], updatedFields[index]];
      setFormData({
        ...formData,
        customFields: updatedFields
      });
    }
  };

  const toggleTemplateSelection = (templateId: string) => {
    setSelectedTemplateIds(prev => {
      if (prev.includes(templateId)) {
        return prev.filter(id => id !== templateId);
      } else {
        return [...prev, templateId];
      }
    });
  };

  // Filter templates based on search term
  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(complianceSearchTerm.toLowerCase()) ||
    template.category.toLowerCase().includes(complianceSearchTerm.toLowerCase())
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Asset Type Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="e.g., Fire Extinguishers, Emergency Lights"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={formData.description || ''}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="Describe this asset type..."
          rows={3}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Compliance Items
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Select one or more compliance items this asset type is tied to
        </p>
        
        {/* Search bar */}
        {templates.length > 0 && (
          <div className="mb-3 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={complianceSearchTerm}
              onChange={(e) => setComplianceSearchTerm(e.target.value)}
              placeholder="Search compliance items..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />
          </div>
        )}
        
        <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
          {templates.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No compliance templates available. Create compliance templates first.
            </p>
          ) : filteredTemplates.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No compliance items match your search.
            </p>
          ) : (
            filteredTemplates.map((template) => (
              <label
                key={template.id}
                className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedTemplateIds.includes(template.id || '')}
                  onChange={() => template.id && toggleTemplateSelection(template.id)}
                  className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">{template.name}</div>
                  <div className="text-xs text-gray-500">
                    {template.category} • {template.frequency}
                  </div>
                </div>
              </label>
            ))
          )}
        </div>
        {selectedTemplateIds.length > 0 && (
          <p className="text-xs text-gray-600 mt-2">
            {selectedTemplateIds.length} compliance item{selectedTemplateIds.length !== 1 ? 's' : ''} selected
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Custom Fields
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Define custom fields to track for each asset of this type (e.g., Serial Number, Purchase Date, Warranty Expiration)
        </p>
        
        {/* Existing Fields */}
        {formData.customFields && formData.customFields.length > 0 && (
          <div className="mb-3 border border-gray-200 rounded-lg p-3 space-y-2 max-h-60 overflow-y-auto">
            {formData.customFields.map((field, index) => (
              <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded hover:bg-gray-100">
                <div className="flex-1">
                  <div className="font-medium text-sm text-gray-900">{field.name}</div>
                  <div className="text-xs text-gray-500">
                    Type: {field.type} {field.required && <span className="text-red-600">• Required</span>}
                    {field.description && ` • ${field.description}`}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => moveField(index, 'up')}
                    disabled={index === 0}
                    className="p-1 text-gray-600 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveField(index, 'down')}
                    disabled={index === formData.customFields!.length - 1}
                    className="p-1 text-gray-600 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeField(index)}
                    className="p-1 text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add New Field */}
        <div className="border border-gray-200 rounded-lg p-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Field Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newFieldName}
                onChange={(e) => setNewFieldName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                placeholder="e.g., Serial Number"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Field Type
              </label>
              <select
                value={newFieldType}
                onChange={(e) => setNewFieldType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              >
                {availableFieldTypes.map(fieldType => (
                  <option key={fieldType.type} value={fieldType.type}>
                    {fieldType.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Description
            </label>
            <input
              type="text"
              value={newFieldDescription}
              onChange={(e) => setNewFieldDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              placeholder="Optional description"
            />
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={newFieldRequired}
                onChange={(e) => setNewFieldRequired(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Required</span>
            </label>
            <button
              type="button"
              onClick={addField}
              className="btn-primary px-4 py-2 flex items-center gap-2 text-sm"
              disabled={!newFieldName.trim()}
            >
              <Plus className="w-4 h-4" />
              Add Field
            </button>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          {assetType ? 'Update' : 'Create'} Asset Type
        </button>
      </div>
    </form>
  );
};

export default AssetTypeForm;

